import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const MESSAGE_TYPE = 'mcp_oauth_result';
const AUTO_CLOSE_MS = 1500;

const IconCheckLarge = () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <polyline
            points="20 6 9 17 4 12"
            stroke="#ffffff"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const IconErrorLarge = () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <line x1="6" y1="6" x2="18" y2="18" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round" />
        <line x1="18" y1="6" x2="6" y2="18" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
);

const IconSpinner = () => (
    <svg
        className="oauth2026__spinner-svg"
        width="32"
        height="32"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
    >
        <circle cx="12" cy="12" r="9" stroke="#ffffff" strokeOpacity="0.25" strokeWidth="3" />
        <path
            d="M21 12a9 9 0 0 0-9-9"
            stroke="#ffffff"
            strokeWidth="3"
            strokeLinecap="round"
        />
    </svg>
);

const PROVIDER_LABELS = {
    notion: 'Notion',
    gmail: 'Gmail',
    slack: 'Slack',
    gdrive: 'Google Drive',
    github: 'GitHub',
};

function parseParams(search) {
    const sp = new URLSearchParams(search);
    const provider =
        sp.get('mcp_connected') ||
        sp.get('provider') ||
        sp.get('mcp_provider') ||
        '';

    const errorReason =
        sp.get('mcp_error') ||
        sp.get('error_description') ||
        sp.get('error') ||
        '';

    const status = errorReason ? 'error' : provider ? 'success' : 'unknown';

    return { provider, errorReason, status };
}

export default function OAuthCallback() {
    const location = useLocation();
    const { provider, errorReason, status } = useMemo(
        () => parseParams(location.search),
        [location.search],
    );
    const [posted, setPosted] = useState(false);
    const [isPopup, setIsPopup] = useState(false);

    const providerLabel = provider
        ? PROVIDER_LABELS[provider] || provider
        : '서비스';

    useEffect(() => {
        const hasOpener =
            typeof window !== 'undefined' && window.opener && window.opener !== window;
        setIsPopup(!!hasOpener);

        if (!hasOpener) return undefined;

        try {
            window.opener.postMessage(
                {
                    type: MESSAGE_TYPE,
                    provider,
                    status: status === 'error' ? 'error' : 'success',
                    reason: status === 'error' ? errorReason : undefined,
                },
                window.location.origin,
            );
            setPosted(true);
        } catch (_) {
            setPosted(false);
        }

        const timer = setTimeout(() => {
            try {
                window.close();
            } catch (_) {}
        }, AUTO_CLOSE_MS);

        return () => clearTimeout(timer);
    }, [provider, errorReason, status]);

    const headline =
        status === 'error'
            ? `${providerLabel} 연결 실패`
            : status === 'success'
                ? `${providerLabel} 연결 완료`
                : 'OAuth 처리 중';

    const description =
        status === 'error'
            ? errorReason || '인증 과정에서 문제가 발생했습니다. 다시 시도해주세요.'
            : status === 'success'
                ? '인증이 완료되었습니다. 이 창은 잠시 후 자동으로 닫힙니다.'
                : '인증 응답을 처리하고 있습니다. 잠시만 기다려주세요.';

    return (
        <>
            <Helmet>
                <title>외부 서비스 연결 - GrowFit</title>
                <meta name="robots" content="noindex,nofollow" />
            </Helmet>

            <div className="oauth2026">
                <main className="oauth2026__main">
                    <div className="oauth2026__brand">
                        <img
                            className="oauth2026__brand-logo"
                            src="/images/growfit-star.png"
                            alt="GrowFit"
                        />
                        <span className="oauth2026__brand-text">GrowFit</span>
                    </div>

                    <section
                        className={`oauth2026__section oauth2026__section--${status}`}
                        aria-live="polite"
                    >
                        <div
                            className={`oauth2026__indicator oauth2026__indicator--${status}`}
                            aria-hidden="true"
                        >
                            {status === 'success' && <IconCheckLarge />}
                            {status === 'error' && <IconErrorLarge />}
                            {status === 'unknown' && <IconSpinner />}
                        </div>

                        <h1 className="oauth2026__title">{headline}</h1>
                        <p className="oauth2026__desc">{description}</p>

                        {!isPopup && status !== 'unknown' && (
                            <div className="oauth2026__actions">
                                <Link
                                    to="/settings/connections"
                                    className="oauth2026__submit"
                                >
                                    외부 연결 설정으로 이동
                                </Link>
                                <Link
                                    to="/user/home"
                                    className="oauth2026__link-btn"
                                >
                                    홈으로 돌아가기
                                </Link>
                            </div>
                        )}

                        {isPopup && status !== 'unknown' && (
                            <button
                                type="button"
                                className="oauth2026__link-btn"
                                onClick={() => {
                                    try { window.close(); } catch (_) {}
                                }}
                            >
                                지금 창 닫기
                            </button>
                        )}

                        {isPopup && posted && (
                            <p className="oauth2026__hint">
                                요청한 화면으로 결과가 전달되었습니다.
                            </p>
                        )}
                    </section>
                </main>
            </div>
        </>
    );
}
